import { useTranslation } from "react-i18next";
import { Workflow, Integration } from "/app/src/models";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { Tooltip, Chip } from "@heroui/react";

interface ReportUsageProps {
  activeWorkflows?: Workflow[];
  inactiveWorkflows?: Workflow[];
  activeIntegrations?: Integration[];
  inactiveIntegrations?: Integration[];
}

/**
 * ReportUsage displays the workflows and integrations that use this report.
 */
export default function ReportUsage({
  activeWorkflows = [],
  inactiveWorkflows = [],
  activeIntegrations = [],
  inactiveIntegrations = [],
}: ReportUsageProps) {
  const { t } = useTranslation();

  const hasWorkflows =
    activeWorkflows.length > 0 || inactiveWorkflows.length > 0;
  const hasIntegrations =
    activeIntegrations.length > 0 || inactiveIntegrations.length > 0;

  if (!hasWorkflows && !hasIntegrations) {
    return null;
  }

  const workflowTooltipContent = (
    <div className="p-2">
      {activeWorkflows.length > 0 && (
        <div className="mb-2">
          <strong>{t("translation:active_workflows_label")}</strong>
          <ul className="list-disc list-inside mt-1 ml-2">
            {activeWorkflows.map((w) => (
              <li key={w.name}>{w.name}</li>
            ))}
          </ul>
        </div>
      )}
      {inactiveWorkflows.length > 0 && (
        <div>
          <strong>{t("translation:inactive_workflows_label")}</strong>
          <ul className="list-disc list-inside mt-1 ml-2">
            {inactiveWorkflows.map((w) => (
              <li key={w.name}>{w.name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const integrationTooltipContent = (
    <div className="p-2">
      {activeIntegrations.length > 0 && (
        <div className="mb-2">
          <strong>{t("translation:active_integrations_label")}</strong>
          <ul className="list-disc list-inside mt-1 ml-2">
            {activeIntegrations.map((i) => (
              <li key={i.name}>{i.name}</li>
            ))}
          </ul>
        </div>
      )}
      {inactiveIntegrations.length > 0 && (
        <div>
          <strong>{t("translation:inactive_integrations_label")}</strong>
          <ul className="list-disc list-inside mt-1 ml-2">
            {inactiveIntegrations.map((i) => (
              <li key={i.name}>{i.name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const totalWorkflows = activeWorkflows.length + inactiveWorkflows.length;
  const totalIntegrations =
    activeIntegrations.length + inactiveIntegrations.length;

  return (
    <>
      {hasWorkflows && (
        <Tooltip content={workflowTooltipContent} placement="bottom">
          <div className="inline-block">
            <Chip
              variant="flat"
              color="default"
              size="sm"
              startContent={<IconBuilder icon="Workflow" size={14} />}
            >
              {totalWorkflows}{" "}
              {totalWorkflows === 1
                ? t("translation:workflow")
                : t("translation:workflows")}
            </Chip>
          </div>
        </Tooltip>
      )}
      {hasIntegrations && (
        <Tooltip content={integrationTooltipContent} placement="bottom">
          <div className="inline-block">
            <Chip
              variant="flat"
              color="default"
              size="sm"
              startContent={<IconBuilder icon="Integration" size={14} />}
            >
              {totalIntegrations}{" "}
              {totalIntegrations === 1
                ? t("translation:integration")
                : t("translation:integrations")}
            </Chip>
          </div>
        </Tooltip>
      )}
    </>
  );
}
